import React, { useState, useEffect } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import LoginScreen from './pages/LoginScreen';
import OnboardingScreen from './pages/OnboardingScreen';
import MainApp from './pages/MainApp';
import SplashScreen from './components/SplashScreen';
import { supabase } from './supabase';
import './index.css';

function AppInner() {
  const { state, dispatch } = useApp();
  const [screen, setScreen] = useState('loading');
  const screenRef = React.useRef('loading');
  const handlingRef = React.useRef(false);

  React.useEffect(() => { screenRef.current = screen; }, [screen]);

  const handleUser = async (user) => {
    try {
      const savedLang = (() => { try { return localStorage.getItem('savely_lang'); } catch(e) { return null; } })();
      const { data: profile } = await supabase
        .from('profiles').select('*').eq('id', user.id).single();

      if (profile) {
        // Ensure we always have a proper username
        const bestUsername = profile.username || user.user_metadata?.full_name || user.email?.split('@')[0] || '';
        const bestAvatar = profile.avatar_emoji || '🌱';
        // Best avatar from all sources
        const userAvatar = profile.avatar_emoji || 
          user.user_metadata?.avatar_emoji || 
          sessionStorage.getItem('savely_avatar') || '🌱';
        const userName = profile.username || 
          user.user_metadata?.full_name || 
          user.email?.split('@')[0] || '';
        dispatch({ type: 'LOGIN', payload: {
          id: user.id, email: user.email,
          name: userName,
          username: userName,
          avatar_emoji: userAvatar,
          theme: profile.theme || 'sapphire',
          referralCode: profile.referral_code || '',
          referralCount: profile.referral_count || 0,
          ambassador: profile.ambassador || false,
        }});
        dispatch({ type: 'LOAD_PROFILE', payload: profile });
        if (profile.plan) dispatch({ type: 'SET_PLAN', payload: profile.plan });
        // localStorage = what user chose now, profile.language = what was saved before
        const lang = savedLang || profile.language || 'en';
        dispatch({ type: 'SET_LANGUAGE', payload: lang });
        // If user chose a different language, update Supabase
        if (savedLang && savedLang !== profile.language) {
          supabase.from('profiles').update({ language: savedLang }).eq('id', user.id).then(() => {}).catch(() => {});
        }
        const isOnboarded = profile.username || profile.income > 0 || profile.xp > 0 || profile.streak > 0 || profile.balance > 0 || profile.language || profile.theme;
        // If profile missing username, save it now
        if (!profile.username && bestUsername) {
          supabase.from('profiles').upsert({ id: user.id, username: bestUsername, avatar_emoji: bestAvatar }).then(() => {});
        }
        setScreen(isOnboarded ? 'app' : 'onboarding');
      } else {
        // Generate unique referral code for new user
        const refCode = (user.email?.split('@')[0] || 'user').toUpperCase().replace(/[^A-Z0-9]/g,'').slice(0,6) + Math.floor(Math.random()*100);
        supabase.from('profiles').update({ referral_code: refCode }).eq('id', user.id).then(() => {}).catch(() => {});

        // Check if user was referred
        try {
          const urlRef = new URLSearchParams(window.location.search).get('ref') || localStorage.getItem('savely_ref');
          if (urlRef) {
            const { data: referrer } = await supabase.from('profiles').select('id').eq('referral_code', urlRef.toUpperCase()).single();
            if (referrer && referrer.id !== user.id) {
              await supabase.from('referrals').insert({ referrer_id: referrer.id, referred_id: user.id, completed: false });
              localStorage.removeItem('savely_ref');
            }
          }
        } catch(e) {}

        dispatch({ type: 'LOGIN', payload: {
          id: user.id, email: user.email,
          name: user.user_metadata?.full_name || user.email?.split('@')[0],
          username: user.email?.split('@')[0] || '',
          avatar_emoji: '🌱',
          theme: 'sapphire',
        }});
        if (savedLang) dispatch({ type: 'SET_LANGUAGE', payload: savedLang });
        setScreen('onboarding');
      }
    } catch(e) {
      console.error('handleUser error:', e);
      try { const l = localStorage.getItem('savely_lang'); if (l) dispatch({ type: 'SET_LANGUAGE', payload: l }); } catch(e2) {}
      // Don't send to onboarding on error - go to login instead
      setScreen('login');
    } finally {
      handlingRef.current = false;
    }
  };

  useEffect(() => {
    // Step 1: Check session immediately
    const init = async () => {
      // Instant path: no token = no session, skip Supabase call entirely
      const hasToken = (() => {
        try {
          const keys = Object.keys(localStorage);
          return keys.some(k => k.includes('supabase') || k.includes('sb-'));
        } catch(e) { return false; }
      })();

      if (!hasToken) {
        const splashDone = (() => { try { return localStorage.getItem('savely_splash_done'); } catch(e) { return null; } })();
        setScreen(splashDone ? 'login' : 'splash');
        return;
      }

      // Has token - check session
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          if (handlingRef.current) return;
          handlingRef.current = true;
          await handleUser(session.user);
        } else {
          const splashDone = (() => { try { return localStorage.getItem('savely_splash_done'); } catch(e) { return null; } })();
          setScreen(splashDone ? 'login' : 'splash');
        }
      } catch(e) {
        console.error('init error:', e);
        setScreen('login');
      }
    };
    init();

    // Step 2: Listen for future auth changes only
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        dispatch({ type: 'LOGOUT' });
        try { localStorage.removeItem('savely_splash_done'); } catch(e) {}
        setScreen('login');
      } else if (event === 'SIGNED_IN' && session?.user) {
        if (screenRef.current === 'app' || screenRef.current === 'onboarding') return;
        if (handlingRef.current) return;
        handlingRef.current = true;
        await handleUser(session.user);
      }
    });

    // Safety timeout - never stuck on loading
    const timeout = setTimeout(() => {
      if (screenRef.current === 'loading') {
        const splashDone = (() => { try { return localStorage.getItem('savely_splash_done'); } catch(e) { return null; } })();
        setScreen(splashDone ? 'login' : 'splash');
      }
    }, 4000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  // Theme
  useEffect(() => {
    const themes = ['sapphire','velvet','lilac','garnet','golden','moondust','neon','aurora','obsidian'];
    themes.forEach(t => document.body.classList.remove(`theme-${t}`));
    if (state.theme) document.body.classList.add(`theme-${state.theme}`);
    if (state.darkMode) document.body.classList.add('dark');
    else document.body.classList.remove('dark');
  }, [state.theme, state.darkMode]);

  // Daily reset
  useEffect(() => {
    if (!state.user) return;
    const today = new Date().toDateString();
    if (state.lastResetDate !== today) dispatch({ type: 'RESET_DAILY' });
    const interval = setInterval(() => {
      if (state.lastResetDate !== new Date().toDateString()) dispatch({ type: 'RESET_DAILY' });
    }, 60000);
    return () => clearInterval(interval);
  }, [state.user, state.lastResetDate]);

  if (screen === 'loading') return (
    <div style={{ minHeight:'100dvh', background:'linear-gradient(145deg,#05020F,#1A0840,#0D1535,#050A20)', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16 }}>
      <div style={{ fontSize:48 }}>🌱</div>
      <div style={{ width:40, height:4, background:'rgba(255,255,255,0.1)', borderRadius:99, overflow:'hidden' }}>
        <div style={{ width:'60%', height:'100%', background:'#22C97A', borderRadius:99, animation:'pulse 1s ease infinite' }}/>
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
    </div>
  );

  if (screen === 'splash') return (
    <SplashScreen onDone={() => {
      try { localStorage.setItem('savely_splash_done', 'true'); } catch(e) {}
      setScreen('login');
    }}/>
  );

  if (screen === 'login') return <LoginScreen onSuccess={() => setScreen('onboarding')}/>; 

  if (screen === 'onboarding') return (
    <OnboardingScreen onDone={() => setScreen('app')}/>
  );

  return <MainApp/>;
}

function CookieBanner() {
  const { state } = useApp();
  const lang = state.language || 'en';
  const [accepted, setAccepted] = React.useState(() => {
    try { return localStorage.getItem('savely_cookies') === 'true'; } catch(e) { return false; }
  });
  if (accepted) return null;
  return (
    <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:99999, background:'rgba(10,10,26,0.97)', backdropFilter:'blur(20px)', padding:'16px 20px', borderTop:'1px solid rgba(255,255,255,0.1)', display:'flex', flexDirection:'column', gap:10 }}>
      <div style={{ fontSize:13, color:'rgba(255,255,255,0.8)', lineHeight:1.5 }}>
        🍪 {lang==='en'?'We use cookies to improve your experience. By using Savely, you agree to our':lang==='sv'?'Vi använder cookies för att förbättra din upplevelse. Genom att använda Savely godkänner du vår':lang==='da'?'Vi bruger cookies for at forbedre din oplevelse. Ved at bruge Savely accepterer du vores':'Vi bruker informasjonskapsler for å forbedre opplevelsen din. Ved å bruke Savely godtar du vår'}{' '}
        <a href="/privacy.html" target="_blank" style={{ color:'#22C97A' }}>{lang==='en'?'privacy policy':lang==='sv'?'integritetspolicy':lang==='da'?'privatlivspolitik':'personvernpolicy'}</a>.
      </div>
      <div style={{ display:'flex', gap:8 }}>
        <button onClick={() => { try { localStorage.setItem('savely_cookies','true'); } catch(e) {} setAccepted(true); }}
          style={{ flex:1, padding:'10px', borderRadius:10, border:'none', background:'#22C97A', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
          {lang==='en'?'Accept':lang==='sv'?'Acceptera':lang==='da'?'Accepter':'Godta'}
        </button>
        <a href="/privacy.html" target="_blank"
          style={{ flex:1, padding:'10px', borderRadius:10, border:'1px solid rgba(255,255,255,0.2)', background:'transparent', color:'rgba(255,255,255,0.6)', fontSize:13, fontWeight:600, cursor:'pointer', textAlign:'center', textDecoration:'none', display:'flex', alignItems:'center', justifyContent:'center' }}>
          {lang==='en'?'Learn more':lang==='sv'?'Läs mer':lang==='da'?'Læs mere':'Les mer'}
        </a>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <AppInner/>
      <CookieBanner/>
    </AppProvider>
  );
}
